import { create } from "zustand";
import { API } from "@/lib/apiConfig";

const API_BASE = API.beneficialGraph;

/**
 * Beneficial Ownership Conflict Graph Store Types
 */
export type NodeType = "person" | "company";
export type RelationType = "owns" | "controls" | "employs" | "related";

export interface OwnershipNode {
  id: string;
  name: string;
  type: NodeType;
  riskScore: number; // 0-100
}

export interface OwnershipEdge {
  from: string;
  to: string;
  relation: RelationType;
}

export interface BeneficialGraphState {
  nodes: OwnershipNode[];
  edges: OwnershipEdge[];
  conflictScore: number; // 0-100
  oligarchClusterScore: number; // 0-100
  nepotismFlags: number;
  tenderCollisionCount: number;
  graphDepth: number;
  integrityRisk: number; // 0-100

  // Actions
  addNode: (name: string, type: NodeType, riskScore: number) => Promise<void>;
  addEdge: (from: string, to: string, relation: RelationType) => Promise<void>;
  removeNode: (id: string) => Promise<void>;
  removeEdge: (from: string, to: string) => Promise<void>;
  flagTenderCollision: () => Promise<void>;

  // Derived calculations
  getGraphDepth: () => number;
  getConflictScore: () => number;
  getOligarchClusterScore: () => number;
  getIntegrityRisk: () => number;

  // Export/Import
  exportOwnershipGraph: () => void;
  importOwnershipGraph: (data: {
    nodes?: OwnershipNode[];
    edges?: OwnershipEdge[];
    nepotismFlags?: number;
    tenderCollisionCount?: number;
  }) => void;
}

/**
 * Helper functions for calculations
 */
function calculateGraphDepth(nodes: OwnershipNode[], edges: OwnershipEdge[]): number {
  if (nodes.length === 0 || edges.length === 0) return 0;

  const visited = new Set<string>();
  let maxDepth = 0;

  const bfs = (startId: string) => {
    const queue: { id: string; depth: number }[] = [{ id: startId, depth: 0 }];
    visited.clear();

    while (queue.length > 0) {
      const { id, depth } = queue.shift()!;
      if (visited.has(id)) continue;
      visited.add(id);

      maxDepth = Math.max(maxDepth, depth);

      edges.forEach((edge) => {
        if (edge.from === id && !visited.has(edge.to)) {
          queue.push({ id: edge.to, depth: depth + 1 });
        }
        if (edge.to === id && !visited.has(edge.from)) {
          queue.push({ id: edge.from, depth: depth + 1 });
        }
      });
    }
  };

  nodes.forEach((node) => {
    bfs(node.id);
  });

  return maxDepth;
}

function calculateConflictScore(nepotismFlags: number, tenderCollisionCount: number, graphDepth: number): number {
  const nepotism = nepotismFlags * 4;
  const tender = tenderCollisionCount * 3;
  const depth = graphDepth * 2;
  const score = nepotism + tender + depth;
  return Math.max(0, Math.min(100, score));
}

function calculateOligarchClusterScore(nodes: OwnershipNode[], edges: OwnershipEdge[]): number {
  if (edges.length === 0 || nodes.length === 0) return 0;

  const avgRiskScore = nodes.reduce((sum, node) => sum + node.riskScore, 0) / nodes.length;
  const score = edges.length * (avgRiskScore / 100);
  return Math.max(0, Math.min(100, score));
}

function calculateIntegrityRisk(conflictScore: number, oligarchClusterScore: number): number {
  const score = conflictScore + oligarchClusterScore * 0.3;
  return Math.max(0, Math.min(100, score));
}

/**
 * Beneficial Ownership Conflict Graph Store (v1)
 * 
 * Manages ownership relationships, conflict detection, and integrity risk scoring
 * for beneficial ownership networks.
 */

export const useBeneficialGraphStore = create<BeneficialGraphState>((set, get) => ({
  nodes: [],
  edges: [],
  conflictScore: 0,
  oligarchClusterScore: 0,
  nepotismFlags: 0,
  tenderCollisionCount: 0,
  graphDepth: 0,
  integrityRisk: 0,

  addNode: async (name, type, riskScore) => {
    // Try to sync with backend if available
    let newNode: OwnershipNode;
    try {
      const response = await fetch(`${API_BASE}/nodes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, type, riskScore }),
      });
      
      if (response.ok) {
        const data = await response.json();
        newNode = data.node;
        // Update state from backend response
        set((state) => ({
          nodes: [...state.nodes, newNode],
          conflictScore: data.state.conflictScore,
          oligarchClusterScore: data.state.oligarchClusterScore,
          graphDepth: data.state.graphDepth,
          integrityRisk: data.state.integrityRisk,
        }));
        return;
      }
    } catch (error) {
      console.log("Backend not available, using local state only");
    }
    
    // Fallback to local creation
    const id = `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    newNode = {
      id,
      name,
      type,
      riskScore: Math.max(0, Math.min(100, riskScore)),
    };

    set((state) => {
      const newNodes = [...state.nodes, newNode];
      
      // Auto-detect nepotism (same name with multiple connections)
      const sameNameCount = newNodes.filter((n) => n.name === name).length;
      const nepotismFlags = sameNameCount > 1 ? state.nepotismFlags + 1 : state.nepotismFlags;

      // Update derived values
      const graphDepth = calculateGraphDepth(newNodes, state.edges);
      const conflictScore = calculateConflictScore(nepotismFlags, state.tenderCollisionCount, graphDepth);
      const oligarchClusterScore = calculateOligarchClusterScore(newNodes, state.edges);
      const integrityRisk = calculateIntegrityRisk(conflictScore, oligarchClusterScore);

      return {
        nodes: newNodes,
        nepotismFlags,
        graphDepth,
        conflictScore,
        oligarchClusterScore,
        integrityRisk,
      };
    });
  },

  addEdge: async (from, to, relation) => {
    // Try to sync with backend if available
    try {
      const response = await fetch(`${API_BASE}/edges`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from, to, relation }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update state from backend response
        set((state) => ({
          edges: [...state.edges, data.edge],
          conflictScore: data.state.conflictScore,
          oligarchClusterScore: data.state.oligarchClusterScore,
          graphDepth: data.state.graphDepth,
          integrityRisk: data.state.integrityRisk,
        }));
        return;
      }
    } catch (error) {
      console.log("Backend not available, using local state only");
    }
    
    // Fallback to local creation
    set((state) => {
      // Check if edge already exists
      const exists = state.edges.some(
        (e) => e.from === from && e.to === to && e.relation === relation
      );
      if (exists) return state;

      const newEdges = [...state.edges, { from, to, relation }];
      
      // Update derived values - use helper functions with updated state
      const graphDepth = calculateGraphDepth(state.nodes, newEdges);
      const conflictScore = calculateConflictScore(state.nepotismFlags, state.tenderCollisionCount, graphDepth);
      const oligarchClusterScore = calculateOligarchClusterScore(state.nodes, newEdges);
      const integrityRisk = calculateIntegrityRisk(conflictScore, oligarchClusterScore);

      return {
        edges: newEdges,
        graphDepth,
        conflictScore,
        oligarchClusterScore,
        integrityRisk,
      };
    });
  },

  removeNode: async (id) => {
    // Try to sync with backend if available
    try {
      const response = await fetch(`${API_BASE}/nodes/${id}`, {
        method: "DELETE",
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update state from backend response
        set(() => data.state);
        return;
      }
    } catch (error) {
      console.log("Backend not available, using local state only");
    }
    
    // Fallback to local deletion
    set((state) => {
      const newNodes = state.nodes.filter((n) => n.id !== id);
      const newEdges = state.edges.filter((e) => e.from !== id && e.to !== id);

      // Update derived values
      const graphDepth = calculateGraphDepth(newNodes, newEdges);
      const conflictScore = calculateConflictScore(state.nepotismFlags, state.tenderCollisionCount, graphDepth);
      const oligarchClusterScore = calculateOligarchClusterScore(newNodes, newEdges);
      const integrityRisk = calculateIntegrityRisk(conflictScore, oligarchClusterScore);

      return {
        nodes: newNodes,
        edges: newEdges,
        graphDepth,
        conflictScore,
        oligarchClusterScore,
        integrityRisk,
      };
    });
  },

  removeEdge: async (from, to) => {
    // Try to sync with backend if available
    try {
      const response = await fetch(`${API_BASE}/edges`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ from, to }),
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update state from backend response
        set(() => data.state);
        return;
      }
    } catch (error) {
      console.log("Backend not available, using local state only");
    }
    
    // Fallback to local deletion
    set((state) => {
      const newEdges = state.edges.filter((e) => !(e.from === from && e.to === to));

      // Update derived values
      const graphDepth = calculateGraphDepth(state.nodes, newEdges);
      const conflictScore = calculateConflictScore(state.nepotismFlags, state.tenderCollisionCount, graphDepth);
      const oligarchClusterScore = calculateOligarchClusterScore(state.nodes, newEdges);
      const integrityRisk = calculateIntegrityRisk(conflictScore, oligarchClusterScore);

      return {
        edges: newEdges,
        graphDepth,
        conflictScore,
        oligarchClusterScore,
        integrityRisk,
      };
    });
  },

  flagTenderCollision: async () => {
    // Try to sync with backend if available
    try {
      const response = await fetch(`${API_BASE}/flag-tender-collision`, {
        method: "POST",
      });
      
      if (response.ok) {
        const data = await response.json();
        // Update state from backend response
        set(() => ({
          tenderCollisionCount: data.tenderCollisionCount,
          conflictScore: data.state.conflictScore,
          integrityRisk: data.state.integrityRisk,
        }));
        return;
      }
    } catch (error) {
      console.log("Backend not available, using local state only");
    }
    
    // Fallback to local update
    set((state) => {
      const newCount = state.tenderCollisionCount + 1;
      const conflictScore = calculateConflictScore(state.nepotismFlags, newCount, state.graphDepth);
      const integrityRisk = calculateIntegrityRisk(conflictScore, state.oligarchClusterScore);

      return {
        tenderCollisionCount: newCount,
        conflictScore,
        integrityRisk,
      };
    });
  },

  getGraphDepth: () => {
    const state = get();
    const { nodes, edges } = state;

    if (nodes.length === 0 || edges.length === 0) return 0;

    // BFS to find maximum depth
    const visited = new Set<string>();
    let maxDepth = 0;

    const bfs = (startId: string) => {
      const queue: { id: string; depth: number }[] = [{ id: startId, depth: 0 }];
      visited.clear();

      while (queue.length > 0) {
        const { id, depth } = queue.shift()!;
        if (visited.has(id)) continue;
        visited.add(id);

        maxDepth = Math.max(maxDepth, depth);

        // Find neighbors
        edges.forEach((edge) => {
          if (edge.from === id && !visited.has(edge.to)) {
            queue.push({ id: edge.to, depth: depth + 1 });
          }
          if (edge.to === id && !visited.has(edge.from)) {
            queue.push({ id: edge.from, depth: depth + 1 });
          }
        });
      }
    };

    // Try from each node to find max depth
    nodes.forEach((node) => {
      bfs(node.id);
    });

    return maxDepth;
  },

  getConflictScore: () => {
    const state = get();
    const nepotism = state.nepotismFlags * 4;
    const tender = state.tenderCollisionCount * 3;
    const depth = state.graphDepth * 2;
    const score = nepotism + tender + depth;
    return Math.max(0, Math.min(100, score));
  },

  getOligarchClusterScore: () => {
    const state = get();
    if (state.edges.length === 0 || state.nodes.length === 0) return 0;

    const avgRiskScore =
      state.nodes.reduce((sum, node) => sum + node.riskScore, 0) / state.nodes.length;
    const score = state.edges.length * (avgRiskScore / 100);
    return Math.max(0, Math.min(100, score));
  },

  getIntegrityRisk: () => {
    const state = get();
    // Use helper functions directly to avoid circular calls
    const conflict = calculateConflictScore(state.nepotismFlags, state.tenderCollisionCount, state.graphDepth);
    const oligarch = calculateOligarchClusterScore(state.nodes, state.edges);
    const score = conflict + oligarch * 0.3;
    return Math.max(0, Math.min(100, score));
  },

  exportOwnershipGraph: () => {
    const state = get();
    const report = {
      nodes: state.nodes,
      edges: state.edges,
      conflictScore: state.getConflictScore(),
      oligarchClusterScore: state.getOligarchClusterScore(),
      integrityRisk: state.getIntegrityRisk(),
      nepotismFlags: state.nepotismFlags,
      tenderCollisionCount: state.tenderCollisionCount,
    };

    const dateStr = new Date().toISOString().split("T")[0].replace(/-/g, "");
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ownershipGraph_${dateStr}.json`;
    a.click();
    URL.revokeObjectURL(url);
  },

  importOwnershipGraph: (data) => {
    set((state) => {
      const newNodes = data.nodes ?? state.nodes;
      const newEdges = data.edges ?? state.edges;
      const newNepotismFlags = data.nepotismFlags ?? state.nepotismFlags;
      const newTenderCollisionCount = data.tenderCollisionCount ?? state.tenderCollisionCount;

      // Recalculate derived values
      const graphDepth = calculateGraphDepth(newNodes, newEdges);
      const conflictScore = calculateConflictScore(newNepotismFlags, newTenderCollisionCount, graphDepth);
      const oligarchClusterScore = calculateOligarchClusterScore(newNodes, newEdges);
      const integrityRisk = calculateIntegrityRisk(conflictScore, oligarchClusterScore);

      return {
        nodes: newNodes,
        edges: newEdges,
        nepotismFlags: newNepotismFlags,
        tenderCollisionCount: newTenderCollisionCount,
        graphDepth,
        conflictScore,
        oligarchClusterScore,
        integrityRisk,
      };
    });
  },
}));

